/**
 * StockLedgerView
 * ─────────────────────────────────────────────────────────────────────────────
 * Vendor-facing immutable stock ledger. Top-level page under the audit feature.
 *
 *   Columns: When | Type | Product | Δ | Balance | Reason | Reference | Actor
 *
 * Filters:
 *   - Type chips (IN / OUT / ADJUST / RETURN / WASTE / TRANSFER)
 *   - Product picker (optional, via productId param)
 *   - Date range (from / to)
 *
 * Actions:
 *   - "Record manual movement" opens a modal where vendor picks a product and
 *     logs a restock / waste / adjustment with reason.
 *   - "Export CSV" dumps the current filter set.
 *   - "Check for drift" per-product → calls reconcile endpoint.
 *
 * Philosophy:
 *   - Read-only by default. Writes only via the modal, which goes through the
 *     single-writer endpoint `/api/stock/movements`.
 *   - Feature-flag gated at the route layer. No UI logic duplicates the ledger
 *     math — balanceBefore/After come from the server.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Badge,
  Spinner,
  Alert,
  Modal,
  Table,
} from "react-bootstrap";
import moment from "moment";
import {
  apiListStockMovements,
  apiListStockProducts,
  apiCreateStockMovement,
  apiCreateStockOpeningBalances,
  apiReconcileStockProduct,
} from "../api/index";
import HelpTooltip from "../components/HelpTooltip";
import "./StockLedgerView.css";
import AppSelect from "../components/ui/AppSelect";

const TYPE_META = {
  IN: { label: "Restock", colour: "#27ae60", icon: "fa-arrow-down" },
  OUT: { label: "Sale", colour: "#2980b9", icon: "fa-arrow-up" },
  ADJUST: { label: "Correction", colour: "#b8860b", icon: "fa-balance-scale" },
  RETURN: { label: "Return", colour: "#16a085", icon: "fa-undo" },
  WASTE: { label: "Waste", colour: "#c0392b", icon: "fa-trash" },
  TRANSFER: { label: "Transfer", colour: "#8e44ad", icon: "fa-exchange-alt" },
};

const PRODUCT_TYPES = [
  "RestaurantProduct",
  "HealthcareProduct",
  "RetailerWholesalerProduct",
  "BrandProduct",
];

const PRODUCT_TYPE_LABELS = {
  RestaurantProduct: "Restaurant menu items",
  HealthcareProduct: "Healthcare listings",
  RetailerWholesalerProduct: "Retail / wholesale listings",
  BrandProduct: "Brand catalog items",
};

function formatNumber(n) {
  if (n == null) return "—";
  return Number(n).toLocaleString();
}

function toCsv(rows) {
  const header = [
    "timestamp",
    "type",
    "product",
    "delta",
    "balanceBefore",
    "balanceAfter",
    "reason",
    "reference",
    "actor",
  ];
  const lines = [header.join(",")];
  for (const r of rows) {
    const line = [
      new Date(r.createdAt).toISOString(),
      r.type,
      (r.productId || ""),
      r.delta,
      r.balanceBefore,
      r.balanceAfter,
      `"${(r.reason || "").replace(/"/g, '""')}"`,
      r.referenceType + (r.referenceId ? `:${r.referenceId}` : ""),
      `"${(r.actorName || "").replace(/"/g, '""')}"`,
    ];
    lines.push(line.join(","));
  }
  return lines.join("\n");
}

function download(filename, contents) {
  const blob = new Blob([contents], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Manual movement modal ─────────────────────────────────────────────────
function ManualMovementModal({ show, onHide, onSaved, initialProduct }) {
  const [form, setForm] = useState({
    productType: "RestaurantProduct",
    productId: "",
    type: "IN",
    delta: "",
    reason: "",
    unitCost: "",
  });
  const [productSearch, setProductSearch] = useState("");
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const changeProductType = (e) => {
    const productType = e.target.value;
    setForm((f) => ({ ...f, productType, productId: "" }));
    setSelectedProduct(null);
    setProductSearch("");
  };
  const pickProduct = (product) => {
    setSelectedProduct(product);
    setProductSearch(product.name);
    setForm((f) => ({
      ...f,
      productType: product.productType,
      productId: product._id,
    }));
  };

  useEffect(() => {
    if (!show || !initialProduct) return;
    pickProduct(initialProduct);
  }, [show, initialProduct]);

  useEffect(() => {
    if (!show) return undefined;
    let active = true;
    setLoadingProducts(true);
    const timer = setTimeout(async () => {
      try {
        const res = await apiListStockProducts({
          productType: form.productType,
          q: selectedProduct?.name === productSearch ? "" : productSearch,
          limit: 12,
        });
        if (active) setProducts(res?.data?.data || []);
      } catch (e) {
        if (active) setProducts([]);
      } finally {
        if (active) setLoadingProducts(false);
      }
    }, 220);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [show, form.productType, productSearch, selectedProduct?.name]);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      let delta = Number(form.delta);
      // Normalize sign for type
      if (form.type === "OUT" || form.type === "WASTE") delta = -Math.abs(delta);
      if (form.type === "IN" || form.type === "RETURN") delta = Math.abs(delta);
      const payload = {
        productType: form.productType,
        productId: form.productId,
        type: form.type,
        delta,
        reason: form.reason || undefined,
        unitCost: form.unitCost ? Number(form.unitCost) : undefined,
      };
      await apiCreateStockMovement(payload);
      onSaved?.();
      onHide?.();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to record movement");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa fa-plus-circle mr-2" /> Record stock movement
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="stock-ledger-modal-note">
          <strong>Use this for inventory only.</strong> Create or edit the product listing
          in Menu/Product Management first, then record restocks, waste, returns, or
          corrections against that existing listing here.
        </div>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Listing group</Form.Label>
                <AppSelect block value={form.productType} onChange={changeProductType}>
                  {PRODUCT_TYPES.map((t) => <option key={t} value={t}>{PRODUCT_TYPE_LABELS[t]}</option>)}
                </AppSelect>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Search existing listing</Form.Label>
                <Form.Control
                  value={productSearch}
                  onChange={(e) => {
                    setProductSearch(e.target.value);
                    setSelectedProduct(null);
                    setForm((f) => ({ ...f, productId: "" }));
                  }}
                  placeholder="Type a product name"
                />
              </Form.Group>
            </Col>
          </Row>
          <div className="stock-ledger-product-picker">
            <div className="stock-ledger-product-picker__header">
              <span>Pick the listing whose stock is changing</span>
              {loadingProducts && <Spinner size="sm" animation="border" />}
            </div>
            {!loadingProducts && products.length === 0 && (
              <div className="stock-ledger-product-picker__empty">
                No matching listings. Add the product in Menu/Product Management before recording stock.
              </div>
            )}
            {products.map((product) => (
              <button
                type="button"
                key={product._id}
                className={`stock-ledger-product-option ${selectedProduct?._id === product._id ? "is-selected" : ""}`}
                onClick={() => pickProduct(product)}
              >
                <span>
                  <strong>{product.name}</strong>
                  <small>{PRODUCT_TYPE_LABELS[product.productType]}</small>
                </span>
                <span>
                  <strong>{formatNumber(product.countInStock)}</strong>
                  <small>in stock</small>
                </span>
              </button>
            ))}
          </div>
          <Row className="mt-2">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Type</Form.Label>
                <AppSelect block value={form.type} onChange={setField("type")}>
                  <option value="IN">Restock (IN)</option>
                  <option value="OUT">Manual sale (OUT)</option>
                  <option value="ADJUST">Correction (ADJUST)</option>
                  <option value="RETURN">Return</option>
                  <option value="WASTE">Waste</option>
                </AppSelect>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Quantity</Form.Label>
                <Form.Control
                  type="number"
                  value={form.delta}
                  onChange={setField("delta")}
                  placeholder="e.g. 5"
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col md={12}>
              <Form.Group>
                <Form.Label>
                  Reason
                  {(form.type === "ADJUST" || form.type === "WASTE") && (
                    <Badge bg="warning" text="dark" className="ms-2">required</Badge>
                  )}
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={form.reason}
                  onChange={setField("reason")}
                  placeholder="Why is this adjustment being made?"
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Unit cost (optional)</Form.Label>
                <Form.Control
                  type="number"
                  value={form.unitCost}
                  onChange={setField("unitCost")}
                  placeholder="for COGS tracking"
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={submitting || !selectedProduct || !form.delta}>
          {submitting ? <Spinner size="sm" animation="border" /> : "Record movement"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ─── Main view ─────────────────────────────────────────────────────────────
export default function StockLedgerView() {
  const [rows, setRows] = useState([]);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [creatingOpening, setCreatingOpening] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ type: "", from: "", to: "", productId: "" });
  const [showModal, setShowModal] = useState(false);
  const [movementProduct, setMovementProduct] = useState(null);

  const load = useCallback(
    async (p = 1) => {
      setLoading(true);
      setError(null);
      try {
        const params = { page: p, limit: 25 };
        if (filters.type) params.type = filters.type;
        if (filters.from) params.from = filters.from;
        if (filters.to) params.to = filters.to;
        if (filters.productId) params.productId = filters.productId;
        const res = await apiListStockMovements(params);
        const payload = res?.data || {};
        const newRows = payload.data || [];
        setRows((prev) => (p === 1 ? newRows : [...prev, ...newRows]));
        setHasMore(!!payload.hasMore);
        setTotal(payload.total || 0);
        setPage(p);
      } catch (e) {
        setError(e?.response?.data?.message || e.message || "Failed to load ledger");
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  useEffect(() => {
    load(1);
  }, [load]);

  const loadCatalog = useCallback(async () => {
    setLoadingCatalog(true);
    try {
      const res = await apiListStockProducts({ productType: "ALL", limit: 50 });
      setCatalogProducts(res?.data?.data || []);
    } catch (e) {
      setCatalogProducts([]);
    } finally {
      setLoadingCatalog(false);
    }
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const totalDelta = useMemo(
    () => rows.reduce((acc, r) => acc + Number(r.delta || 0), 0),
    [rows]
  );

  const ledgerSummary = useMemo(() => {
    const inbound = rows
      .filter((r) => Number(r.delta || 0) > 0)
      .reduce((acc, r) => acc + Number(r.delta || 0), 0);
    const outbound = rows
      .filter((r) => Number(r.delta || 0) < 0)
      .reduce((acc, r) => acc + Math.abs(Number(r.delta || 0)), 0);
    const uniqueProducts = new Set(rows.map((r) => String(r.productId || ""))).size;
    const latest = rows[0]?.createdAt ? moment(rows[0].createdAt).format("MMM D, h:mm A") : "No activity";
    const typeCounts = Object.keys(TYPE_META).map((type) => ({
      type,
      count: rows.filter((r) => r.type === type).length,
    }));
    const maxTypeCount = Math.max(1, ...typeCounts.map((item) => item.count));

    return {
      inbound,
      outbound,
      uniqueProducts,
      latest,
      typeCounts,
      maxTypeCount,
    };
  }, [rows]);

  const catalogSummary = useMemo(() => {
    const totalStock = catalogProducts.reduce((acc, product) => acc + Number(product.countInStock || 0), 0);
    return { totalStock };
  }, [catalogProducts]);

  const exportCsv = () => {
    download(`stock-ledger-${Date.now()}.csv`, toCsv(rows));
  };

  const openMovementForProduct = (product) => {
    setMovementProduct(product || null);
    setShowModal(true);
  };

  const createOpeningBalances = async () => {
    setCreatingOpening(true);
    setError(null);
    try {
      await apiCreateStockOpeningBalances({ productType: "ALL" });
      await Promise.all([load(1), loadCatalog()]);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to create opening balances");
    } finally {
      setCreatingOpening(false);
    }
  };

  return (
    <Container fluid className="stock-ledger-page py-3">
      <div className="stock-ledger-shell">
        <div className="stock-ledger-hero">
          <div>
            <div className="stock-ledger-eyebrow">Audit & Compliance</div>
            <h2>
              Stock Ledger
              <HelpTooltip text="Every change to your stock — sales, restocks, waste, corrections — is permanently recorded here. Use this to reconcile with physical counts, prove COGS for financing, or catch theft." />
            </h2>
            <p>
              Catalog listings are created in Menu/Product Management. This page records the stock changes
              behind those listings.
            </p>
          </div>
          <div className="stock-ledger-actions">
            <Button className="stock-ledger-btn stock-ledger-btn--ghost" size="sm" onClick={exportCsv} disabled={!rows.length}>
              <i className="fa fa-download mr-1" /> Export CSV
            </Button>
            <Button className="stock-ledger-btn stock-ledger-btn--primary" size="sm" onClick={() => openMovementForProduct(null)}>
              <i className="fa fa-plus mr-1" /> Record movement
            </Button>
          </div>
        </div>

        <Row className="stock-ledger-kpis">
          <Col lg={3} md={6}>
            <div className="stock-ledger-kpi">
              <span>Movements shown</span>
              <strong>{formatNumber(rows.length)}</strong>
              <small>of {formatNumber(total)} total</small>
            </div>
          </Col>
          <Col lg={3} md={6}>
            <div className="stock-ledger-kpi">
              <span>Net change</span>
              <strong className={totalDelta >= 0 ? "is-positive" : "is-negative"}>
                {totalDelta >= 0 ? "+" : ""}{formatNumber(totalDelta)}
              </strong>
              <small>current filter set</small>
            </div>
          </Col>
          <Col lg={3} md={6}>
            <div className="stock-ledger-kpi">
              <span>Inbound / Outbound</span>
              <strong>{formatNumber(ledgerSummary.inbound)} / {formatNumber(ledgerSummary.outbound)}</strong>
              <small>quantity movement</small>
            </div>
          </Col>
          <Col lg={3} md={6}>
            <div className="stock-ledger-kpi">
              <span>Products touched</span>
              <strong>{formatNumber(catalogProducts.length)}</strong>
              <small>{formatNumber(catalogSummary.totalStock)} units in catalog</small>
            </div>
          </Col>
        </Row>

        <Row className="stock-ledger-main">
          <Col xl={3} lg={4}>
            <Card className="stock-ledger-panel">
              <Card.Body>
                <div className="stock-ledger-flow">
                  <div>
                    <span>1</span>
                    <strong>Create the listing</strong>
                    <small>Name, photo, price, section, options.</small>
                  </div>
                  <div>
                    <span>2</span>
                    <strong>Record stock changes</strong>
                    <small>Restock, sale, waste, return, correction.</small>
                  </div>
                </div>
                <div className="stock-ledger-panel-title">
                  <i className="fa fa-filter" /> Slicers
                </div>
                <Form.Group className="stock-ledger-slicer">
                  <Form.Label>Movement type</Form.Label>
                  <AppSelect block
                    size="sm"
                    value={filters.type}
                    onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))}
                  >
                    <option value="">All types</option>
                    {Object.keys(TYPE_META).map((t) => (
                      <option key={t} value={t}>{TYPE_META[t].label}</option>
                    ))}
                  </AppSelect>
                </Form.Group>
                <Form.Group className="stock-ledger-slicer">
                  <Form.Label>From</Form.Label>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={filters.from}
                    onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
                  />
                </Form.Group>
                <Form.Group className="stock-ledger-slicer">
                  <Form.Label>To</Form.Label>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={filters.to}
                    onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
                  />
                </Form.Group>
                <Form.Group className="stock-ledger-slicer">
                  <Form.Label>Listing ID</Form.Label>
                  <Form.Control
                    size="sm"
                    value={filters.productId}
                    onChange={(e) => setFilters((f) => ({ ...f, productId: e.target.value }))}
                    placeholder="Optional listing id"
                  />
                </Form.Group>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="stock-ledger-reset"
                  onClick={() => setFilters({ type: "", from: "", to: "", productId: "" })}
                >
                  Clear slicers
                </Button>

                <div className="stock-ledger-panel-title stock-ledger-panel-title--spaced">
                  <i className="fa fa-chart-bar" /> Movement mix
                </div>
                <div className="stock-ledger-bars">
                  {ledgerSummary.typeCounts.map(({ type, count }) => {
                    const meta = TYPE_META[type];
                    return (
                      <div className="stock-ledger-bar-row" key={type}>
                        <span>{meta.label}</span>
                        <div>
                          <i
                            style={{
                              width: `${Math.max(4, (count / ledgerSummary.maxTypeCount) * 100)}%`,
                              background: meta.colour,
                            }}
                          />
                        </div>
                        <strong>{count}</strong>
                      </div>
                    );
                  })}
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={9} lg={8}>
            <Card className="stock-ledger-panel stock-ledger-catalog-card">
              <Card.Header>
                <div>
                  <strong>Current Catalog Inventory</strong>
                  <span>These are the menu/product listings already connected to stock counts</span>
                </div>
                <div className="stock-ledger-catalog-actions">
                  {loadingCatalog && <Spinner size="sm" animation="border" />}
                  <Button
                    className="stock-ledger-btn stock-ledger-btn--ghost"
                    size="sm"
                    onClick={createOpeningBalances}
                    disabled={creatingOpening || !catalogProducts.length}
                  >
                    {creatingOpening ? <Spinner size="sm" animation="border" /> : "Create opening balances"}
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                {catalogProducts.length === 0 && !loadingCatalog ? (
                  <div className="stock-ledger-catalog-empty">
                    No catalog products were found for this vendor. Add products from Restaurant > Menu first.
                  </div>
                ) : (
                  <div className="stock-ledger-catalog-grid">
                    {catalogProducts.slice(0, 8).map((product) => (
                      <button
                        type="button"
                        className="stock-ledger-catalog-item"
                        key={`${product.productType}-${product._id}`}
                        onClick={() => openMovementForProduct(product)}
                      >
                        <span>
                          <strong>{product.name}</strong>
                          <small>{PRODUCT_TYPE_LABELS[product.productType]}</small>
                        </span>
                        <span>
                          <strong>{formatNumber(product.countInStock)}</strong>
                          <small>in stock</small>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {catalogProducts.length > 0 && rows.length === 0 && (
                  <div className="stock-ledger-catalog-note">
                    You have products in the catalog, but no immutable stock history yet.
                    Use opening balances once to bring the current counts into the ledger.
                  </div>
                )}
              </Card.Body>
            </Card>

            <Card className="stock-ledger-panel stock-ledger-table-card">
              <Card.Header>
                <div>
                  <strong>Movement Register</strong>
                  <span>Showing {rows.length} records in the current report view</span>
                </div>
                {loading && <Spinner size="sm" animation="border" />}
              </Card.Header>
              <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}

              {rows.length === 0 && !loading && !error && (
                <div className="stock-ledger-empty">
                  <div className="stock-ledger-empty-icon">
                    <i className="fa fa-cubes" />
                  </div>
                  <strong>No stock movements yet</strong>
                  <div>
                    Once you record a restock or a sale reduces stock, the movement
                    will appear here. Every row is permanent.
                  </div>
                  <Button className="stock-ledger-btn stock-ledger-btn--primary mt-3" size="sm" onClick={() => openMovementForProduct(null)}>
                    Record first movement
                  </Button>
                </div>
              )}

              {rows.length > 0 && (
                <Table hover responsive size="sm" className="stock-ledger-table mb-0">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Type</th>
                      <th>Product</th>
                      <th className="text-end">Δ</th>
                      <th className="text-end">Balance</th>
                      <th>Reason</th>
                      <th>Reference</th>
                      <th>Actor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const meta = TYPE_META[r.type] || { label: r.type, colour: "#666", icon: "fa-circle" };
                      const deltaColour = r.delta >= 0 ? "#27ae60" : "#c0392b";
                      return (
                        <tr key={r._id}>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {moment(r.createdAt).format("MMM D, h:mm A")}
                          </td>
                          <td>
                            <Badge style={{ background: meta.colour }}>
                              <i className={`fa ${meta.icon}`} style={{ marginRight: 4 }} />
                              {meta.label}
                            </Badge>
                          </td>
                          <td>
                            <span className="stock-ledger-product-type">{r.productType}</span>
                            <div className="stock-ledger-mono">{String(r.productId).slice(-8)}</div>
                          </td>
                          <td className="text-end" style={{ color: deltaColour, fontWeight: 600 }}>
                            {r.delta >= 0 ? "+" : ""}{r.delta}
                          </td>
                          <td className="text-end">
                            <span className="text-muted">{r.balanceBefore} →</span> <strong>{r.balanceAfter}</strong>
                          </td>
                          <td>{r.reason || <span className="text-muted">—</span>}</td>
                          <td>
                            {r.referenceType}
                            {r.referenceId && <div className="stock-ledger-mono">{String(r.referenceId).slice(-8)}</div>}
                          </td>
                          <td>{r.actorName || <span className="text-muted">system</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}

              {loading && (
                <div className="text-center py-3">
                  <Spinner size="sm" animation="border" /> Loading…
                </div>
              )}

              {!loading && hasMore && (
                <div className="text-center mt-3">
                  <Button className="stock-ledger-btn stock-ledger-btn--ghost" size="sm" onClick={() => load(page + 1)}>
                    Load more
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
          </Col>
        </Row>
      </div>

      <ManualMovementModal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setMovementProduct(null);
        }}
        onSaved={() => {
          load(1);
          loadCatalog();
        }}
        initialProduct={movementProduct}
      />
    </Container>
  );
}
